import { mdiEye, mdiLinkVariantOff } from "@mdi/js";
import { Button, Modal, Space } from "antd";
import { DeviceVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import IconButton from "components/ui/IconButton";
import useDumbTable from "hooks/useDumbTable";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomTable from "widgets/table/CustomTable";
import { ExtendedColumnType } from "widgets/table/table-settings/ExtendedColumnType";
import { ColumnConfigParams } from "widgets/table/useTableUtils";

const { confirm } = Modal;

const MaintainedDevicesTab: React.FC = () => {
  const { t } = useTranslation();
  const { specialPlace, assignedMaintainedDevices } = useSelector(
    (state) => state.specialPlacesView
  );
  const { fetchAssignedMaintainedDevices } = useSelector(
    (state) => state.loading.effects.specialPlacesView
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const columnParams = useMemo<ColumnConfigParams<DeviceVm>[]>(
    () => [
      {
        key: "name",
        sort: false,
        title: t("maintainedDevice.name"),
      },
    ],
    [t]
  );

  const handleDetachButtonClick = useCallback(
    async (id?: string) => {
      confirm({
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t("specialPlace.confirmDeatachMaintainedDeviceModal.title"),
        onOk: async () => {
          if (typeof specialPlace?.id !== "string" || typeof id !== "string") {
            return;
          }

          await dispatch.specialPlacesView.detachMaintainedDevices([id]);

          dispatch.specialPlacesView.fetchAssignedMaintainedDevices(
            specialPlace.id
          );
        },
      });
    },
    [dispatch.specialPlacesView, specialPlace?.id, t]
  );

  const handleViewButtonClick = useCallback(
    (id?: string) => {
      navigate(`/maintained-devices/${id}`);
    },
    [navigate]
  );

  const handleDetachAllMaintainedDeviceClick = useCallback(() => {
    confirm({
      okText: t("common.confirmDeleteModal.yes"),
      okType: "danger",
      cancelText: t("common.confirmDeleteModal.no"),
      title: t("specialPlace.confirmDetachAllMaintainedDeviceModal.title"),
      onOk: async () => {
        if (typeof specialPlace?.id !== "string") {
          return;
        }

        await dispatch.specialPlacesView.detachAllMaintainedDevice(
          specialPlace.id
        );

        dispatch.specialPlacesView.fetchAssignedMaintainedDevices(
          specialPlace.id
        );
      },
    });
  }, [dispatch.specialPlacesView, specialPlace?.id, t]);

  const actionColumnParams = useMemo<
    Partial<ExtendedColumnType<DeviceVm>> | undefined
  >(
    () => ({
      fixed: "right",
      columnOrderFixed: true,
      width: 90,
      render(item: any, record) {
        return (
          <Space>
            <IconButton
              path={mdiEye}
              onClick={() => handleViewButtonClick(record.id)}
              title={t("common.details")}
            />
            <IconButton
              path={mdiLinkVariantOff}
              onClick={() => handleDetachButtonClick(record.id)}
              title={t("maintainedDevice.deleteLinking")}
            />
          </Space>
        );
      },
      title: t("common.table.actions"),
    }),
    [handleDetachButtonClick, handleViewButtonClick, t]
  );

  const customTableProps = useDumbTable({
    columnParams,
    list: assignedMaintainedDevices,
    actionColumnParams,
    loading: fetchAssignedMaintainedDevices.loading,
    headerOptions: (
      <Button
        danger
        htmlType="submit"
        block
        icon={<MaterialIcon path={mdiLinkVariantOff} />}
        onClick={() => handleDetachAllMaintainedDeviceClick()}
        disabled={!assignedMaintainedDevices.length}
      >
        {t("specialPlace.detachAllDevice")}
      </Button>
    ),
  });

  useEffect(() => {
    if (typeof specialPlace?.id !== "string") {
      return;
    }

    dispatch.specialPlacesView.fetchAssignedMaintainedDevices(specialPlace?.id);

    return () => {
      dispatch.specialPlacesView.setAssignedMaintainedDevices([]);
    };
  }, [dispatch.specialPlacesView, specialPlace?.id]);

  return (
    <CustomTable<DeviceVm>
      {...customTableProps}
      selectable={false}
      scroll={{ x: 1000 }}
    />
  );
};

export default MaintainedDevicesTab;
